import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input, Card, Heading, BodyText, Skeleton } from '@aaimplatform/ui';
import type { InputProps } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';
import { CHA_TIERS, CHA_TIER_CLASSES, CHA_TIER_LABELS } from './chaTier';
import { NO_CLASSES_AVAILABLE } from './AssetClassPoliciesTab';
import { PercentInput } from './PercentInput';

type AssetClassPolicy = components['schemas']['AssetClassPolicy'];
type ChaThresholds = components['schemas']['ChaThresholds'];
type ActionWindows = components['schemas']['ActionWindows'];
type PerformanceAdjustment = components['schemas']['PerformanceAdjustment'];

interface ChaThresholdsTabProps {
  policies: AssetClassPolicy[];
  onChange: (policies: AssetClassPolicy[]) => void;
  /**
   * True when the platform asset-class flags could not be read, so an empty
   * list does not mean no class is enabled (r3 fi-g7-4).
   */
  availabilityUnreadable?: boolean;
  /**
   * True while the platform asset-class flags are still loading. The page
   * filters to flag-enabled classes, so an empty list during the read is not
   * "no class enabled" and shows a loading state instead.
   */
  availabilityLoading?: boolean;
  /**
   * Called with false while any whole-number field on the tab holds an entry
   * that is not a valid value (an emptied field included), and with true once
   * every field is valid again. The page holds Save while it reads false.
   */
  onValidityChange?: (valid: boolean) => void;
}

/**
 * The whole-number entry a field holds, or undefined when the text is empty,
 * not a whole number, or outside the spec bounds (collateral-policy.openapi.yaml
 * ActionWindows, PerformanceAdjustment, EscalationStep). Number('') is 0, so
 * reading the text with Number() stored an emptied field as 0 (ruling 71).
 */
export function parseWholeNumber(raw: string, min: number, max?: number): number | undefined {
  const text = raw.trim();
  if (!/^\d+$/.test(text)) return undefined;
  const value = Number(text);
  if (value < min || (max !== undefined && value > max)) return undefined;
  return value;
}

/** The inline error a whole-number field shows while its entry is not valid. */
export function wholeNumberError(unit: string, min: number, max?: number): string {
  return max !== undefined
    ? `Enter a whole number of ${unit} from ${min} to ${max}.`
    : `Enter a whole number of ${unit}, ${min} or more.`;
}

interface WholeNumberInputProps
  extends Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'type' | 'error' | 'min' | 'max'> {
  /** The stored whole number. */
  value: number;
  /** Called with a valid entry only; an emptied or out-of-range field never reaches the policy. */
  onChange: (value: number) => void;
  /** Called with false while the typed entry is not valid, and with true once it is or the field unmounts. */
  onValidityChange?: (valid: boolean) => void;
  /** The spec minimum. */
  min: number;
  /** The spec maximum, where one is declared. */
  max?: number;
  /** The unit the inline error names ('hours', 'months'). */
  unit: string;
}

/**
 * A number input over a stored whole number.
 *
 * It keeps the typed text while the entry is invalid, so an emptied field stays
 * empty with an inline error rather than snapping to a value, and it reports
 * only valid entries upward. The stored value is untouched while the field is
 * invalid, and the page holds Save through onValidityChange.
 */
export function WholeNumberInput({
  value,
  onChange,
  onValidityChange,
  min,
  max,
  unit,
  ...inputProps
}: WholeNumberInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const invalid = draft !== null && parseWholeNumber(draft, min, max) === undefined;

  const validityRef = useRef(onValidityChange);
  validityRef.current = onValidityChange;
  useEffect(() => {
    validityRef.current?.(!invalid);
  }, [invalid]);
  // An unmounted field drops its draft, so the stored value stands again.
  useEffect(() => () => validityRef.current?.(true), []);

  return (
    <Input
      {...inputProps}
      type="number"
      min={min}
      max={max}
      step={1}
      value={draft ?? String(value)}
      error={invalid ? wholeNumberError(unit, min, max) : undefined}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const parsed = parseWholeNumber(raw, min, max);
        if (parsed !== undefined) onChange(parsed);
      }}
      // A valid entry formats back from the stored value; an invalid one keeps
      // its text and its error until it is corrected.
      onBlur={() => {
        if (!invalid) setDraft(null);
      }}
    />
  );
}

/**
 * Tracks which whole-number fields on a tab are invalid and reports the tab's
 * validity through onValidityChange: false while any field is invalid, true
 * once none is, and true when the tab unmounts (its drafts are gone). Returns
 * the per-field reporter a WholeNumberInput calls.
 */
export function useFieldValidity(onValidityChange?: (valid: boolean) => void) {
  const [invalidFields, setInvalidFields] = useState<ReadonlySet<string>>(() => new Set());
  const report = useCallback((field: string, valid: boolean) => {
    setInvalidFields((prev) => {
      if (valid ? !prev.has(field) : prev.has(field)) return prev;
      const next = new Set(prev);
      if (valid) next.delete(field);
      else next.add(field);
      return next;
    });
  }, []);

  const callbackRef = useRef(onValidityChange);
  callbackRef.current = onValidityChange;
  const allValid = invalidFields.size === 0;
  useEffect(() => {
    callbackRef.current?.(allValid);
  }, [allValid]);
  useEffect(() => () => callbackRef.current?.(true), []);

  return report;
}

/**
 * The short form beside an action-window label. Days or weeks only for an
 * exact multiple; otherwise hours, so 36 hours reads 36h rather than a rounded
 * 2d (r3 fi-g7-14).
 */
export function hoursToDisplay(hours: number): string {
  if (hours > 0 && hours % 168 === 0) return `${hours / 168}w`;
  if (hours > 0 && hours % 24 === 0) return `${hours / 24}d`;
  return `${hours}h`;
}

const TIER_DESCRIPTIONS = {
  GREEN: 'Healthy. At or below target LTV.',
  YELLOW: 'Watch. Monitoring active, borrower has time.',
  ORANGE: 'Action required. Your institution is notified, borrower must act.',
  RED: 'Critical. Terminal escalation. Handed to your institution. No auto-liquidation.',
} as const;

/** Field label chrome; the color, when a field belongs to a tier, comes from CHA_TIER_CLASSES. */
const FIELD_LABEL = 'block text-label-xs font-semibold uppercase mb-1';

/**
 * ChaThresholdsTab - CHA tier thresholds, action windows, and performance adjustment
 *
 * This is the 4-tier CHA model. RED is terminal -- no auto-liquidation. RED
 * runs from the orange ceiling to 100% LTV (iris cw-06); redLtv is the FI's
 * escalation point inside RED, drawn as a tick on the bar, not as a band edge.
 * Action windows define how long a borrower has at each tier before escalation.
 * Performance adjustment modulates windows based on borrower payment history.
 *
 * Every input carries an id its visible label points at (htmlFor), so a screen
 * reader announces "Yellow ceiling" rather than an unnamed number field
 * (UI run 2026-09-27, fi-g7-10). The Aaim-baseline notice lives on
 * CollateralPolicyPage, once for every policy route (fi-g7-1).
 */
export function ChaThresholdsTab({
  policies,
  onChange,
  availabilityUnreadable = false,
  availabilityLoading = false,
  onValidityChange,
}: ChaThresholdsTabProps) {
  const enabledPolicies = policies.filter(p => p.enabled);
  const reportValidity = useFieldValidity(onValidityChange);

  const handleThresholdChange = (
    index: number,
    field: keyof ChaThresholds,
    value: number,
  ) => {
    const updated = policies.map((p, i) => {
      if (i !== index) return p;
      return { ...p, chaThresholds: { ...p.chaThresholds, [field]: value } };
    });
    onChange(updated);
  };

  const handleWindowChange = (
    index: number,
    field: keyof ActionWindows,
    value: number,
  ) => {
    const updated = policies.map((p, i) => {
      if (i !== index) return p;
      return { ...p, actionWindows: { ...p.actionWindows, [field]: value } };
    });
    onChange(updated);
  };

  // The performance section renders only when the class carries a
  // performanceAdjustment, so an edit always has a whole object to change. No
  // invented defaults stand in for an absent one (fi-g7-17).
  const handlePerformanceChange = (
    index: number,
    field: keyof PerformanceAdjustment,
    value: number,
  ) => {
    const updated = policies.map((p, i) => {
      if (i !== index || !p.performanceAdjustment) return p;
      return { ...p, performanceAdjustment: { ...p.performanceAdjustment, [field]: value } };
    });
    onChange(updated);
  };

  // Find the actual index in the full policies array for an enabled policy
  const fullIndex = (enabledPolicy: AssetClassPolicy) =>
    policies.findIndex(p => p.taxonomyNodeId === enabledPolicy.taxonomyNodeId);

  return (
    <div className="space-y-8">
      {/* Page title lives in the sticky topnav (AppHeader); the body opens
          with the descriptive subtitle, no redundant H1. Reset and Save sit in
          one row on CollateralPolicyPage for every editor (r3 fi-g7-11). */}
      <BodyText variant="ui" size="sm" color="secondary" className="max-w-2xl">
        Set the LTV thresholds that place each pledge in a Collateral Health Alert (CHA) tier and how long each tier allows before escalation.
      </BodyText>

      {/* CHA Tier Reference */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {CHA_TIERS.map(tier => (
          <div
            key={tier}
            data-testid={`cha-tier-card-${tier}`}
            className={`${CHA_TIER_CLASSES[tier].panel} border ${CHA_TIER_CLASSES[tier].border} rounded-sm p-3`}
          >
            <div className={`text-xs font-semibold uppercase tracking-wider ${CHA_TIER_CLASSES[tier].text} mb-1`}>
              {CHA_TIER_LABELS[tier]}
            </div>
            <BodyText variant="ui" size="xs" color="secondary">
              {TIER_DESCRIPTIONS[tier]}
            </BodyText>
          </div>
        ))}
      </div>

      {/* Per-asset-class thresholds */}
      {enabledPolicies.map(policy => {
        const idx = fullIndex(policy);
        const perf = policy.performanceAdjustment;
        const fieldId = (field: string) => `cha-${policy.taxonomyNodeId}-${field}`;
        return (
          <Card key={policy.taxonomyNodeId}>
            <div className="px-5 py-4">
              {/* Semantic level 2: the page h1 is the AppHeader title and these
                  cards are the first headings beneath it. as={4} keeps the
                  sans card-title face the level-4 heading had. */}
              <Heading level={2} as={4} size="card" className="mb-5">{policy.displayName}</Heading>

              {/* LTV Thresholds grid */}
              <div className="mb-6">
                <BodyText variant="ui" size="xs" color="tertiary" className="uppercase tracking-wider mb-3">
                  LTV Thresholds (%)
                </BodyText>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor={fieldId('targetLtv')} className={`${FIELD_LABEL} ${CHA_TIER_CLASSES.GREEN.text}`}>
                      Green ceiling
                    </label>
                    <PercentInput
                      id={fieldId('targetLtv')}
                      value={policy.chaThresholds.targetLtv}
                      onChange={(ratio) => { if (ratio !== undefined) handleThresholdChange(idx, 'targetLtv', ratio); }}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor={fieldId('yellowLtv')} className={`${FIELD_LABEL} ${CHA_TIER_CLASSES.YELLOW.text}`}>
                      Yellow ceiling
                    </label>
                    <PercentInput
                      id={fieldId('yellowLtv')}
                      value={policy.chaThresholds.yellowLtv}
                      onChange={(ratio) => { if (ratio !== undefined) handleThresholdChange(idx, 'yellowLtv', ratio); }}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor={fieldId('orangeLtv')} className={`${FIELD_LABEL} ${CHA_TIER_CLASSES.ORANGE.text}`}>
                      Orange ceiling
                    </label>
                    <PercentInput
                      id={fieldId('orangeLtv')}
                      value={policy.chaThresholds.orangeLtv}
                      onChange={(ratio) => { if (ratio !== undefined) handleThresholdChange(idx, 'orangeLtv', ratio); }}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    {/* Not a tier boundary (operator ruling cha-red-start-and-thresholds):
                        RED starts above the orange ceiling. redLtv is where the FI
                        escalates a RED pledge, so its label takes no tier color. */}
                    <label htmlFor={fieldId('redLtv')} className={`${FIELD_LABEL} text-tertiary`}>
                      Escalation point
                    </label>
                    <PercentInput
                      id={fieldId('redLtv')}
                      value={policy.chaThresholds.redLtv}
                      onChange={(ratio) => { if (ratio !== undefined) handleThresholdChange(idx, 'redLtv', ratio); }}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Visual bar. RED is drawn from the orange ceiling to 100% LTV,
                    because RED has no ceiling of its own; the escalation point
                    is a tick inside it. */}
                <div className="relative mt-3 h-3 bg-canvas rounded-full overflow-hidden flex" aria-hidden="true">
                  <div className={`${CHA_TIER_CLASSES.GREEN.bar} h-full`} style={{ width: `${policy.chaThresholds.targetLtv * 100}%` }} />
                  <div className={`${CHA_TIER_CLASSES.YELLOW.bar} h-full`} style={{ width: `${(policy.chaThresholds.yellowLtv - policy.chaThresholds.targetLtv) * 100}%` }} />
                  <div className={`${CHA_TIER_CLASSES.ORANGE.bar} h-full`} style={{ width: `${(policy.chaThresholds.orangeLtv - policy.chaThresholds.yellowLtv) * 100}%` }} />
                  <div
                    data-testid={`cha-bar-red-${policy.taxonomyNodeId}`}
                    className={`${CHA_TIER_CLASSES.RED.bar} h-full`}
                    style={{ width: `${(1 - policy.chaThresholds.orangeLtv) * 100}%` }}
                  />
                  <div
                    data-testid={`cha-bar-escalation-tick-${policy.taxonomyNodeId}`}
                    className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-surface"
                    style={{ left: `${policy.chaThresholds.redLtv * 100}%` }}
                  />
                </div>
              </div>

              {/* Action Windows */}
              <div className="mb-6">
                <BodyText variant="ui" size="xs" color="tertiary" className="uppercase tracking-wider mb-3">
                  Action Windows (hours)
                </BodyText>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor={fieldId('yellowReviewHours')} className={`${FIELD_LABEL} ${CHA_TIER_CLASSES.YELLOW.text}`}>
                      Yellow review ({hoursToDisplay(policy.actionWindows.yellowReviewHours)})
                    </label>
                    <WholeNumberInput
                      id={fieldId('yellowReviewHours')}
                      value={policy.actionWindows.yellowReviewHours}
                      onChange={(hours) => handleWindowChange(idx, 'yellowReviewHours', hours)}
                      onValidityChange={(valid) => reportValidity(fieldId('yellowReviewHours'), valid)}
                      min={1}
                      unit="hours"
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor={fieldId('orangeActionHours')} className={`${FIELD_LABEL} ${CHA_TIER_CLASSES.ORANGE.text}`}>
                      Orange action ({hoursToDisplay(policy.actionWindows.orangeActionHours)})
                    </label>
                    <WholeNumberInput
                      id={fieldId('orangeActionHours')}
                      value={policy.actionWindows.orangeActionHours}
                      onChange={(hours) => handleWindowChange(idx, 'orangeActionHours', hours)}
                      onValidityChange={(valid) => reportValidity(fieldId('orangeActionHours'), valid)}
                      min={1}
                      unit="hours"
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor={fieldId('redEscalationHours')} className={`${FIELD_LABEL} ${CHA_TIER_CLASSES.RED.text}`}>
                      Red escalation ({hoursToDisplay(policy.actionWindows.redEscalationHours)})
                    </label>
                    <WholeNumberInput
                      id={fieldId('redEscalationHours')}
                      value={policy.actionWindows.redEscalationHours}
                      onChange={(hours) => handleWindowChange(idx, 'redEscalationHours', hours)}
                      onValidityChange={(valid) => reportValidity(fieldId('redEscalationHours'), valid)}
                      min={1}
                      unit="hours"
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor={fieldId('graceExtensionMaxHours')} className={`${FIELD_LABEL} text-tertiary`}>
                      Grace extension max ({hoursToDisplay(policy.actionWindows.graceExtensionMaxHours)})
                    </label>
                    <WholeNumberInput
                      id={fieldId('graceExtensionMaxHours')}
                      value={policy.actionWindows.graceExtensionMaxHours}
                      onChange={(hours) => handleWindowChange(idx, 'graceExtensionMaxHours', hours)}
                      onValidityChange={(valid) => reportValidity(fieldId('graceExtensionMaxHours'), valid)}
                      min={0}
                      unit="hours"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Performance Adjustment. Neutral labels: these are settings,
                  not tiers or alerts, so they carry no green or red. */}
              {perf ? (
                <div>
                  <BodyText variant="ui" size="xs" color="tertiary" className="uppercase tracking-wider mb-3">
                    Performance Adjustment (Borrower Seasoning)
                  </BodyText>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label htmlFor={fieldId('strongHistoryExtensionPct')} className={`${FIELD_LABEL} text-tertiary`}>
                        Strong history extension (%)
                      </label>
                      <PercentInput
                        id={fieldId('strongHistoryExtensionPct')}
                        value={perf.strongHistoryExtensionPct}
                        onChange={(ratio) => { if (ratio !== undefined) handlePerformanceChange(idx, 'strongHistoryExtensionPct', ratio); }}
                        className="font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor={fieldId('weakHistoryReductionPct')} className={`${FIELD_LABEL} text-tertiary`}>
                        Weak history reduction (%)
                      </label>
                      <PercentInput
                        id={fieldId('weakHistoryReductionPct')}
                        value={perf.weakHistoryReductionPct}
                        onChange={(ratio) => { if (ratio !== undefined) handlePerformanceChange(idx, 'weakHistoryReductionPct', ratio); }}
                        className="font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor={fieldId('seasoningThresholdMonths')} className={`${FIELD_LABEL} text-tertiary`}>
                        Seasoning threshold (months)
                      </label>
                      <WholeNumberInput
                        id={fieldId('seasoningThresholdMonths')}
                        value={perf.seasoningThresholdMonths}
                        onChange={(months) => handlePerformanceChange(idx, 'seasoningThresholdMonths', months)}
                        onValidityChange={(valid) => reportValidity(fieldId('seasoningThresholdMonths'), valid)}
                        min={1}
                        max={60}
                        unit="months"
                        className="font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor={fieldId('paymentConsistencyMinPct')} className={`${FIELD_LABEL} text-tertiary`}>
                        Min payment consistency (%)
                      </label>
                      <PercentInput
                        id={fieldId('paymentConsistencyMinPct')}
                        value={perf.paymentConsistencyMinPct}
                        onChange={(ratio) => { if (ratio !== undefined) handlePerformanceChange(idx, 'paymentConsistencyMinPct', ratio); }}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <BodyText variant="ui" size="xs" color="tertiary">
                  Performance adjustment: not set for this asset class.
                </BodyText>
              )}
            </div>
          </Card>
        );
      })}

      {enabledPolicies.length === 0 && availabilityLoading && (
        <div role="status" className="space-y-4">
          <span className="sr-only">Loading asset classes</span>
          <Skeleton height={192} className="w-full" />
          <Skeleton height={192} className="w-full" />
        </div>
      )}

      {/* The directive to Asset Class Eligibility appears only when that page
          lists a class to accept, and links to it. With no class available
          it sent the FI admin to an empty page (GH #677). */}
      {enabledPolicies.length === 0 && !availabilityLoading && (
        <div className="text-center py-12">
          <BodyText variant="ui" color="tertiary">
            {availabilityUnreadable ? (
              'Asset-class availability could not be read, so no classes are shown.'
            ) : policies.length === 0 ? (
              NO_CLASSES_AVAILABLE
            ) : (
              <>
                No asset classes enabled. Enable at least one class under{' '}
                <Link to="/policy/asset-classes" className="text-accent hover:underline">
                  Asset Class Eligibility
                </Link>
                .
              </>
            )}
          </BodyText>
        </div>
      )}
    </div>
  );
}
